import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, FlexboxGrid, Form, SelectPicker } from "rsuite";

import "./CustomerFilters.css";
import { updateIn } from "json-immut";
import { HelperServices } from "../../../core/services/HelperServices";

import { UtilityServices } from "../../../core/services/UtilityServices";
import { StoreServices } from "../../../core/services/StoreServices";
import { CustomerServices } from "../../../core/services/CustomerServices";
import { get } from "lodash/fp";
import { BulkCustomerFields, CustomerListParams } from "./CustomerDataTypes";
import { messageTypeArr, roleTypeArr } from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { isEmptyVal, isNonNegativeInteger } from "../../../utils/helpers";

export interface PropTypesInterface {
  checkedKeys: any;
  setCheckedKeys: Function;
  onBulkImport: Function;
  data: any;
  mobileLength?: number;
}

export default function CustomerBulkParams({
  checkedKeys,
  setCheckedKeys,
  onBulkImport,
  data,

  mobileLength = 10,
}: PropTypesInterface) {
  const { t } = useTranslation();
  const defaultSettings = UtilityServices.getLoggedInUser("settings");
  const selectedCompany = useSelector((state: any) => state.company);
  const initFormData = {
    company_id: "",
    store_id: UtilityServices.getLoggedInUser("store_id"),
    area_id: "",
    building_id: "",
    city_id: !isEmptyVal(defaultSettings["default_city"])
      ? parseInt(defaultSettings["default_city"])
      : "",
    state_id: !isEmptyVal(defaultSettings["default_state"])
      ? parseInt(defaultSettings["default_state"])
      : "",
    country_id: !isEmptyVal(defaultSettings["default_country"])
      ? parseInt(defaultSettings["default_country"])
      : "",
    customer_source: "",
    customers: "",
  };
  const [formData, setFormData] = useState<BulkCustomerFields>(initFormData);
  const [openBulkActionNoSelectionModal, setOpenBulkActionNoSelectionModal] =
    useState(false);
  const [loader, setLoading] = useState(true);
  const [customerSourceList, setCustomerSourceList] = useState([] as any);
  const [cityList, setCityList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [areaList, setAreaList] = useState([]);
  const [buildingList, setBuildingList] = useState([]);
  const [storeList, setStoreList] = useState([]);

  const countryList = useSelector((state: any) => state.country.data);

  useEffect(() => {
    if (data) {
      //console.log(data);
    }
  }, [data]);

  function handleFormChanges(data: any) {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  }

  function init() {
    try {
      storeSearch("");
      getCustomerSource();
      loadUserDefaultSettings();
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (selectedCompany.id) {
      storeSearch("");
      getCustomerSource();
    }
  }, [selectedCompany]);

  function loadUserDefaultSettings() {
    setLoading(true);

    let obj = { ...formData };
    preSelectFields(obj);
    setLoading(false);
  }

  async function preSelectFields(obj: any) {
    if (obj.country_id) {
      await handleCountryChange(obj.country_id);
    }
    if (obj.state_id) {
      await handleStateChange(obj.state_id);
    }
    if (obj?.city_id && obj?.state_id && obj?.country_id) {
      await handleCityChange(obj.city_id);
    }
  }

  function getCompanyId(forComp?: number) {
    let compId: number = 0;
    if (UtilityServices.getLoggedInUser("user_type") == roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (
      UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend
    ) {
      compId = forComp ? forComp : selectedCompany.id;
    }
    return compId;
  }

  async function getCustomerSource() {
    let compId = getCompanyId();
    if (!compId) {
      return;
    }
    try {
      const res = await CustomerServices.getCustomerSources({
        company: compId,
      });
      const respData = get("data.data", res);
      setCustomerSourceList(respData);
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function storeSearch(searchStore: string, forComp?: number) {
    let compId = getCompanyId(forComp);

    if (!compId) {
      return;
    }
    try {
      const res = await StoreServices.getStores({
        company: compId,
        smart_search: searchStore,
      });
      const respData = get("data.data", res);
      if (respData.length) {
        setStoreList(respData);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function handleCountryChange(countryId: number) {
    try {
      setLoading(true);
      const stateRes = await HelperServices.getAllValidStates({
        country: countryId,
      });
      setStateList(get("data.data", stateRes));
      resetSelectionFor("state");
      handleFormChanges({ path: "country_id", value: countryId });
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  }

  async function handleStateChange(stateId: number) {
    try {
      setLoading(true);
      const res = await HelperServices.getAllValidCities({ state: stateId });
      setCityList(res.data.data);
      resetSelectionFor("city");
      handleFormChanges({ path: "state_id", value: stateId });
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  }

  async function handleCityChange(cityId: number) {
    try {
      setLoading(true);
      const res = await HelperServices.getAllValidAreas({ city: cityId });
      setAreaList(res.data.data);
      resetSelectionFor("area");
      handleFormChanges({ path: "city_id", value: cityId });
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  }

  async function handleAreaChange(areaId: number) {
    try {
      setLoading(true);
      const res = await HelperServices.getAllValidBuildings({ area: areaId });
      setBuildingList(res.data.data);
      resetSelectionFor("building");
      handleFormChanges({ path: "area_id", value: areaId });
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  }

  function clearForm() {
    //resetSelectionFor("country");
    setFormData(initFormData);
  }

  function resetSelectionFor(resetFor: string) {
    let resetData: Object = {};
    switch (resetFor) {
      case "country":
        resetData = {
          country: "",
          state: "",
          city: "",
          area: "",
          building: "",
        };
        setStateList([]);
        setCityList([]);
        setAreaList([]);
        setBuildingList([]);
        break;
      case "state":
        resetData = {
          state: "",
          city: "",
          area: "",
          building: "",
        };
        setCityList([]);
        setAreaList([]);
        setBuildingList([]);
        break;
      case "city":
        resetData = {
          city: "",
          area: "",
          building: "",
        };
        setAreaList([]);
        setBuildingList([]);
        break;
      case "area":
        resetData = {
          area: "",
          building: "",
        };
        setBuildingList([]);
        break;
      case "building":
        resetData = {
          building: "",
        };
        break;
    }
    let newFormData = {
      ...formData,
      ...resetData,
    };
    setFormData(newFormData);
  }

  function bulkImport() {
    let compId = getCompanyId();
    if (!compId) {
      UtilityServices.showToaster({
        message: t("REQUIRED_SELECT", {
          field: t("COMPANY").toLowerCase(),
        }),
        type: messageTypeArr.error,
      });
      return;
    } else {
      formData.company_id = String(compId);
    }
    if (!formData.city_id) {
      UtilityServices.showToaster({
        message: t("REQUIRED_SELECT", {
          field: t("CITY").toLowerCase(),
        }),
        type: messageTypeArr.error,
      });
      return;
    }
    if (checkedKeys.length === 0) {
      setOpenBulkActionNoSelectionModal(true);
      return true;
    }
    let importData: any[] = [];
    let validItem = true;
    data.forEach(function (item: any) {
      if (checkedKeys.includes(item.id)) {
        validItem = true;
        if (isEmptyVal(item.mobile)) {
          UtilityServices.showToaster({
            message:
              t("REQUIRED", {
                field: t("MOBILE").toLowerCase(),
              }) +
              " " +
              t("AT") +
              " " +
              t("REC_NO") +
              " " +
              item.row_no,
            type: messageTypeArr.error,
          });
          validItem = false;
        }
        if (
          item.mobile.length != mobileLength ||
          !isNonNegativeInteger(item.mobile)
        ) {
          UtilityServices.showToaster({
            message:
              t("VALID", {
                entity: t("MOBILE").toLowerCase(),
              }) +
              " " +
              t("AT") +
              " " +
              t("REC_NO") +
              " " +
              item.row_no,
            type: messageTypeArr.error,
          });
          validItem = false;
        }
        if (isEmptyVal(item.fname)) {
          UtilityServices.showToaster({
            message:
              t("REQUIRED", {
                field: t("NAME").toLowerCase(),
              }) +
              " " +
              t("AT") +
              " " +
              t("REC_NO") +
              " " +
              item.row_no,
            type: messageTypeArr.error,
          });
          validItem = false;
        }
        if (validItem) importData.push(item);
      }
    });
    if (importData.length > 0) {
      formData.customers = JSON.stringify(importData);
      onBulkImport && onBulkImport(formData);
    }
  }

  return (
    <div>
      <div className="content mb-3 pb-0">
        <Form
          formValue={formData}
          onChange={(data: any) => {
            let form = { ...formData, ...data };
            setFormData(form);
          }}
          layout="inline"
        >
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item>
              <Form.Group controlId="country_id">
                <Form.ControlLabel>{t("COUNTRY")}</Form.ControlLabel>
                <SelectPicker
                  data={countryList}
                  valueKey="id"
                  labelKey="country_name"
                  onSelect={(value, item) => {
                    handleCountryChange(value);
                  }}
                  onClean={(e: any) => {
                    resetSelectionFor("country");
                  }}
                  name="country_id"
                  value={formData?.country_id}
                />
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item>
              <Form.Group controlId="state_id">
                <Form.ControlLabel>{t("STATE")}</Form.ControlLabel>
                <SelectPicker
                  data={stateList}
                  valueKey="id"
                  labelKey="state_name"
                  onSelect={(value, item) => {
                    handleStateChange(value);
                  }}
                  onClean={(e: any) => {
                    resetSelectionFor("state");
                  }}
                  name="state_id"
                  value={formData?.state_id}
                />
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item>
              <Form.Group controlId="city_id">
                <Form.ControlLabel>{t("CITY")}</Form.ControlLabel>
                <SelectPicker
                  data={cityList}
                  valueKey="id"
                  labelKey="city_name"
                  onSelect={(value, item) => {
                    handleCityChange(value);
                  }}
                  onClean={(e: any) => {
                    resetSelectionFor("city");
                  }}
                  name="city"
                  value={formData?.city_id}
                />
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item>
              <Form.Group controlId="area_id">
                <Form.ControlLabel>{t("AREA")}</Form.ControlLabel>
                <SelectPicker
                  data={areaList}
                  valueKey="id"
                  labelKey="area_name"
                  onSelect={(value, item) => {
                    handleAreaChange(value);
                  }}
                  onClean={(e: any) => {
                    resetSelectionFor("area");
                  }}
                  name="area"
                  value={formData?.area_id}
                />
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item>
              <Form.Group controlId="building_id">
                <Form.ControlLabel>{t("BUILDING")}</Form.ControlLabel>
                <SelectPicker
                  data={buildingList}
                  valueKey="id"
                  labelKey="building_name"
                  onSelect={(value, item) => {
                    handleFormChanges({ path: "building_id", value });
                  }}
                  onClean={(e: any) => {
                    resetSelectionFor("building");
                  }}
                  name="building_id"
                  value={formData?.building_id}
                />
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item>
              <Form.Group controlId="store_id">
                <Form.ControlLabel>{t("STORE")}</Form.ControlLabel>
                <SelectPicker
                  data={storeList}
                  onSearch={(e: any) => {
                    if (e) {
                      storeSearch(e);
                    }
                  }}
                  valueKey="id"
                  labelKey="store_name"
                  onSelect={(value, item) => {
                    handleFormChanges({ path: "store_id", value });
                  }}
                  onClean={(e: any) => {
                    handleFormChanges({ path: "store_id", value: "" });
                  }}
                  name="store_id"
                  value={formData?.store_id}
                />
              </Form.Group>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item>
              <Form.Group controlId="customer_source_id">
                <Form.ControlLabel>
                  {t("CUSTOMER")} {t("SOURCE")}
                </Form.ControlLabel>
                <SelectPicker
                  data={customerSourceList}
                  valueKey="id"
                  labelKey="source_name"
                  onSelect={(value, item) => {
                    handleFormChanges({
                      path: "customer_source_id",
                      value,
                    });
                  }}
                  onClean={(e: any) => {
                    handleFormChanges({
                      path: "customer_source_id",
                      value: "",
                    });
                  }}
                  name="customer_source_id"
                  value={formData?.customer_source_id}
                />
              </Form.Group>
            </FlexboxGrid.Item>
          </FlexboxGrid>

          <div className="filter-btn">
            <Button onClick={() => clearForm()}>
              {t("CLEAR")} {t("FORM")}
            </Button>
            <Button onClick={() => bulkImport()} color="blue" type="button">
              {t("IMPORT")}
            </Button>
          </div>
        </Form>
        <AlertModal
          open={openBulkActionNoSelectionModal}
          handleClose={(e: any) => {
            if (e) {
              setOpenBulkActionNoSelectionModal(false);
            }
          }}
          displayText={t("PLEASE_SELECT_RECORD_TO_ACTION")}
          modalTitle={t("PLEASE_SELECT")}
          cancelButton={false}
        />
      </div>
    </div>
  );
}
